import {
  AttractionThemeImageCell,
  GenerateDescriptionsDialog,
  GenerateVoiceoversDialog,
  ThemeStatusBadge,
} from '@/components/attractions/themes';
import { VoiceoverAudioPlayer } from '@/components/shared/VoiceoverAudioPlayer';
import { type AttractionTheme } from '@/config/objects/attraction-themes';
import {
  Button,
  EmptyTableState,
  Loading,
  RichTextContent,
  formatDate,
  useApiClient,
} from '@startupquickstart/frontend';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FileText, Sparkles, Volume2 } from 'lucide-react';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';

interface ThemeResponse {
  success: boolean;
  data: AttractionTheme;
}

/**
 * Overview tab inside the inline theme detail. Mirrors
 * `<AttractionOverviewTab>`'s shape: AI action buttons on top, then a
 * 5-column grid where the left column carries the description cards
 * (each with its own voiceover) and the right column carries an Image
 * card + Theme Information card (in place of Location/Contact for an
 * attraction, since a theme has neither).
 *
 * Mounted at `/attractions/:id/themes/:themeId/overview` as a child of
 * the theme layout.
 */
export function AttractionThemeOverviewTab() {
  const { id: attractionId, themeId } = useParams<{
    id: string;
    themeId: string;
  }>();
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const [descriptionsDialogOpen, setDescriptionsDialogOpen] = useState(false);
  const [voiceoversDialogOpen, setVoiceoversDialogOpen] = useState(false);

  const { data, isLoading } = useQuery<ThemeResponse>({
    queryKey: ['AttractionTheme', attractionId, themeId],
    queryFn: () =>
      apiClient.get<ThemeResponse>(
        `/attractions/${attractionId}/themes/${themeId}`,
      ),
    enabled: Boolean(attractionId && themeId),
  });
  const theme = data?.data;

  function refetchTheme(): void {
    queryClient.invalidateQueries({
      queryKey: ['AttractionTheme', attractionId, themeId],
    });
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loading />
      </div>
    );
  }

  if (!attractionId || !themeId || !theme) {
    return (
      <EmptyTableState
        title="Theme not found"
        description="This theme may have been deleted."
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-3">
        <Button
          variant="outline"
          onClick={() => setDescriptionsDialogOpen(true)}
        >
          <Sparkles className="h-4 w-4" />
          Generate Descriptions
        </Button>
        <Button variant="outline" onClick={() => setVoiceoversDialogOpen(true)}>
          <Volume2 className="h-4 w-4" />
          Generate Voiceovers
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Left — descriptions */}
        <div className="lg:col-span-3 space-y-6">
          <div className="rounded-xl border bg-card shadow-sm">
            <div className="px-6 py-4 border-b">
              <h2 className="text-lg font-medium tracking-tight text-zinc-900">
                Description
              </h2>
              <p className="text-sm text-muted-foreground">
                Themed short description used in listings and previews
              </p>
            </div>
            <div className="p-6 space-y-6">
              <div>
                <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5">
                  <Volume2 className="h-3.5 w-3.5 shrink-0" />
                  Voiceover
                </div>
                {theme.voiceover ? (
                  <VoiceoverAudioPlayer
                    voiceover={theme.voiceover}
                    isInSync={theme.isDescriptionVoiceoverInSync ?? null}
                    outOfSyncMessage="Description has changed since voiceover was generated"
                  />
                ) : (
                  <div className="flex items-center justify-between rounded-lg border border-dashed px-4 py-3">
                    <p className="text-sm text-muted-foreground">
                      No voiceover generated
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setVoiceoversDialogOpen(true)}
                    >
                      <Sparkles className="h-3.5 w-3.5" />
                      Generate
                    </Button>
                  </div>
                )}
              </div>

              <div>
                <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-2">
                  Content
                </div>
                {theme.description ? (
                  <RichTextContent content={theme.description} />
                ) : (
                  <EmptyTableState
                    icon={<FileText className="h-6 w-6" />}
                    title="No description"
                    description="Edit to add a description, or click Generate Descriptions"
                  />
                )}
              </div>
            </div>
            <div className="border-t px-6 py-4 text-sm text-muted-foreground flex flex-wrap gap-x-6 gap-y-1">
              <span>Created: {formatDate(theme.createdAt)}</span>
              <span>Updated: {formatDate(theme.updatedAt)}</span>
            </div>
          </div>

          <div className="rounded-xl border bg-card shadow-sm">
            <div className="px-6 py-4 border-b">
              <h2 className="text-lg font-medium tracking-tight text-zinc-900">
                Long Description
              </h2>
              <p className="text-sm text-muted-foreground">
                Detailed themed description used in the full attraction view
              </p>
            </div>
            <div className="p-6 space-y-6">
              <div>
                <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5">
                  <Volume2 className="h-3.5 w-3.5 shrink-0" />
                  Voiceover
                </div>
                {theme.longVoiceover ? (
                  <VoiceoverAudioPlayer
                    voiceover={theme.longVoiceover}
                    isInSync={theme.isLongDescriptionVoiceoverInSync ?? null}
                    outOfSyncMessage="Long description has changed since voiceover was generated"
                  />
                ) : (
                  <div className="flex items-center justify-between rounded-lg border border-dashed px-4 py-3">
                    <p className="text-sm text-muted-foreground">
                      No voiceover generated
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setVoiceoversDialogOpen(true)}
                    >
                      <Sparkles className="h-3.5 w-3.5" />
                      Generate
                    </Button>
                  </div>
                )}
              </div>

              <div>
                <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-2">
                  Content
                </div>
                {theme.longDescription ? (
                  <RichTextContent content={theme.longDescription} />
                ) : (
                  <EmptyTableState
                    icon={<FileText className="h-6 w-6" />}
                    title="No long description"
                    description="Edit to add one, or click Generate Descriptions"
                  />
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Right — image (replaces location/contact for an attraction) + theme info */}
        <div className="lg:col-span-2 space-y-6">
          <div className="rounded-xl border bg-card shadow-sm">
            <div className="px-6 py-4 border-b">
              <h2 className="text-lg font-medium tracking-tight text-zinc-900">
                Theme Image
              </h2>
              <p className="text-sm text-muted-foreground">
                Featured image for this theme (overrides the attraction default)
              </p>
            </div>
            <div className="p-6">
              <AttractionThemeImageCell attractionTheme={theme} size="lg" />
            </div>
          </div>

          <div className="rounded-xl border bg-card shadow-sm">
            <div className="px-6 py-4 border-b">
              <h2 className="text-lg font-medium tracking-tight text-zinc-900">
                Theme Information
              </h2>
              <p className="text-sm text-muted-foreground">
                Theme type and configuration
              </p>
            </div>
            <div className="p-6">
              <div className="divide-y">
                <div className="py-3 first:pt-0">
                  <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-0.5">
                    Theme Type
                  </div>
                  <div className="text-sm">
                    {theme.themeType ? (
                      <Link
                        className="text-primary hover:underline"
                        to={`/theme-types/${theme.themeType.id}`}
                      >
                        {theme.themeType.name}
                      </Link>
                    ) : (
                      <span className="text-muted-foreground">Not set</span>
                    )}
                  </div>
                </div>
                <div className="py-3">
                  <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">
                    Status
                  </div>
                  <ThemeStatusBadge status={theme.status} />
                </div>
                <div className="py-3">
                  <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-0.5">
                    Name Override
                  </div>
                  <div className="text-sm">
                    {theme.name || (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <GenerateDescriptionsDialog
        attractionId={attractionId}
        themeId={themeId}
        open={descriptionsDialogOpen}
        onClose={() => {
          setDescriptionsDialogOpen(false);
          refetchTheme();
        }}
      />

      <GenerateVoiceoversDialog
        attractionId={attractionId}
        themeId={themeId}
        open={voiceoversDialogOpen}
        onClose={() => setVoiceoversDialogOpen(false)}
        onSuccess={refetchTheme}
      />
    </div>
  );
}
